import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Download, Loader2, Shield, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadMagnetModal
 * Props:
 *   open {bool}
 *   onClose {fn}
 *   source {string}  — identificador de dónde se abre (ej. "seguridad-agentes")
 */
export default function LeadMagnetModal({ open, onClose, source = 'web' }) {
  const [email, setEmail]       = useState('');
  const [name, setName]         = useState('');
  const [status, setStatus]     = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg('Introduce un email válido.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/lead-magnet/checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), source }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
        // Auto-trigger download
        setTimeout(() => {
          const a = document.createElement('a');
          a.href = `${process.env.REACT_APP_BACKEND_URL}${data.download_path}`;
          a.download = 'checklist-seguridad-neuralia369.pdf';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }, 600);
      } else {
        setStatus('error');
        setErrorMsg('Error al procesar la solicitud. Inténtalo de nuevo.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Error de conexión. Inténtalo de nuevo.');
    }
  };

  const reset = () => {
    setEmail(''); setName(''); setStatus('idle'); setErrorMsg('');
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={reset}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            data-testid="lead-magnet-backdrop"
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="fixed inset-0 flex items-center justify-center z-50 px-4 pointer-events-none"
          >
            <div className="relative w-full max-w-md rounded-2xl border border-white/12 bg-[#0D1E35] shadow-2xl pointer-events-auto overflow-hidden" data-testid="lead-magnet-modal">
              {/* Gradient accent top */}
              <div className="h-1 w-full bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400" />

              {/* Close */}
              <button onClick={reset} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors" data-testid="lead-magnet-close">
                <X className="w-5 h-5" />
              </button>

              <div className="p-7">
                {status !== 'success' ? (
                  <>
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-cyan-300" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-300/80 mb-0.5">Recurso gratuito</p>
                        <h3 className="text-white font-black text-base leading-tight">Checklist de Seguridad<br />para Agentes de IA</h3>
                      </div>
                    </div>

                    {/* What's inside */}
                    <ul className="space-y-2 mb-6">
                      {[
                        '6 pilares de seguridad con preguntas de evaluación',
                        'Preguntas concretas para evaluar cualquier proveedor',
                        'Datos verificados: CrowdStrike, Verizon DBIR, Microsoft 2025',
                      ].map(item => (
                        <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1.5">Nombre (opcional)</label>
                        <input
                          type="text"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="Tu nombre"
                          className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 transition-colors"
                          data-testid="lead-magnet-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 mb-1.5">Email profesional <span className="text-red-400">*</span></label>
                        <input
                          type="email"
                          value={email}
                          onChange={e => { setEmail(e.target.value); setErrorMsg(''); }}
                          placeholder="tu@empresa.com"
                          required
                          className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 transition-colors"
                          data-testid="lead-magnet-email-input"
                        />
                        {errorMsg && <p className="text-red-400 text-xs mt-1">{errorMsg}</p>}
                      </div>

                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full btn-primary justify-center mt-1"
                        data-testid="lead-magnet-submit-btn"
                      >
                        {status === 'loading' ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Procesando…</>
                        ) : (
                          <><Download className="w-4 h-4" /> Descargar PDF gratis</>
                        )}
                      </button>
                      <p className="text-[11px] text-slate-500 text-center">
                        Sin spam. Sin contratos. Solo el checklist.
                      </p>
                    </form>
                  </>
                ) : (
                  /* Success state */
                  <div className="text-center py-4" data-testid="lead-magnet-success">
                    <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', duration: 0.5 }}
                      className="w-14 h-14 rounded-full bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle2 className="w-7 h-7 text-cyan-400" />
                    </motion.div>
                    <h3 className="text-white font-black text-xl mb-2">¡Descarga iniciada!</h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-5">
                      Tu PDF se está descargando. Si no se inicia automáticamente,
                      usa el enlace de abajo.
                    </p>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/checklist-seguridad-neuralia369.pdf`}
                      download="checklist-seguridad-neuralia369.pdf"
                      className="btn-primary justify-center w-full"
                      data-testid="lead-magnet-download-link"
                    >
                      <Download className="w-4 h-4" /> Descargar ahora
                    </a>
                    <button onClick={reset} className="mt-3 text-sm text-slate-400 hover:text-white transition-colors">
                      Cerrar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
